import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Title, Text, Grid, Card, Badge, TextInput } from '@tremor/react';
import { MessageSquare } from 'lucide-react';
import { KPICard } from '@/components/KPICard';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage, buildQueryString } from '@/lib/api';
import {
  formatRangeShort,
  useDateRange,
  useDateRangeKey,
} from '@/lib/dateRange';

interface ChatStats {
  totals: { messages: number; interactions: number; users: number; errors: number };
  by_status: Array<{ status: string; count: number }>;
  by_model: Array<{ model: string; count: number }>;
  by_day: Array<{ day: string; messages: number; interactions: number; users: number }>;
}

interface ChatMessage {
  id: number;
  interaction_id: string;
  org_id: number | null;
  org_name: string | null;
  user_id: number | null;
  user_email: string | null;
  role: string;
  content: string;
  model_name: string | null;
  status: string | null;
  error_message: string | null;
  created_at: string | null;
}

interface MessagesResponse {
  items: ChatMessage[];
  total: number;
}

const fmtInt = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : new Intl.NumberFormat('pt-BR').format(n);

export function ChatPage() {
  const { range, apiParams } = useDateRange();
  const rangeKey = useDateRangeKey();
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const stats = useQuery<ChatStats>({
    queryKey: ['chat.stats', rangeKey],
    queryFn: async () =>
      (await api().get('/chat/stats', { params: apiParams })).data,
  });

  const messages = useQuery<MessagesResponse>({
    queryKey: ['chat.messages', debouncedQuery, statusFilter, rangeKey],
    queryFn: async () => {
      const qs = buildQueryString({
        ...apiParams,
        per_page: 50,
        q: debouncedQuery || undefined,
        status: statusFilter || undefined,
      });
      return (await api().get(`/chat/messages${qs}`)).data;
    },
  });

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <Title>Chat Audit</Title>
        <Text>Logs de conversas com LLM · {formatRangeShort(range)}.</Text>
      </header>

      <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
        <KPICard
          label={`Mensagens · ${formatRangeShort(range)}`}
          value={stats.isLoading ? null : fmtInt(stats.data?.totals.messages)}
          loading={stats.isLoading}
        />
        <KPICard
          label="Interações"
          value={stats.isLoading ? null : fmtInt(stats.data?.totals.interactions)}
          loading={stats.isLoading}
          hint="Threads únicas"
        />
        <KPICard
          label="Usuários"
          value={stats.isLoading ? null : fmtInt(stats.data?.totals.users)}
          loading={stats.isLoading}
        />
        <KPICard
          label="Erros"
          value={stats.isLoading ? null : fmtInt(stats.data?.totals.errors)}
          loading={stats.isLoading}
          hint="Status error/failed"
          error={stats.data && stats.data.totals.errors > 0 ? `${stats.data.totals.errors} falhas` : undefined}
        />
      </Grid>

      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card>
          <Title className="text-base">Por modelo</Title>
          {stats.isLoading && <LoadingSkeleton rows={3} />}
          {!stats.isLoading && (
            <div className="mt-3 space-y-1">
              {stats.data?.by_model.map((m) => (
                <div key={m.model} className="flex items-center justify-between border-b border-slate-800/50 py-1.5 text-sm">
                  <code className="text-xs text-slate-300">{m.model}</code>
                  <span className="font-mono text-slate-200">{fmtInt(m.count)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card>
          <Title className="text-base">Por status</Title>
          {stats.isLoading && <LoadingSkeleton rows={3} />}
          {!stats.isLoading && (
            <div className="mt-3 space-y-1">
              {stats.data?.by_status.map((s) => (
                <div key={s.status} className="flex items-center justify-between border-b border-slate-800/50 py-1.5 text-sm">
                  <Badge color={s.status === 'success' || s.status === 'ok' ? 'emerald' : s.status.includes('error') || s.status.includes('fail') ? 'red' : 'slate'}>
                    {s.status}
                  </Badge>
                  <span className="font-mono text-slate-200">{fmtInt(s.count)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </Grid>

      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-blue-300" />
            <Title className="text-base">Mensagens</Title>
            {messages.data && (
              <Text className="text-xs text-slate-400">
                {messages.data.total} total
              </Text>
            )}
          </div>
          <Grid numItems={2} className="gap-2 w-96">
            <TextInput
              placeholder="Buscar texto..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                clearTimeout((window as unknown as { _t?: number })._t);
                (window as unknown as { _t?: number })._t = window.setTimeout(
                  () => setDebouncedQuery(e.target.value),
                  300,
                );
              }}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm"
            >
              <option value="">Todos status</option>
              {stats.data?.by_status.map((s) => (
                <option key={s.status} value={s.status}>{s.status}</option>
              ))}
            </select>
          </Grid>
        </div>
        {messages.isLoading && <LoadingSkeleton rows={5} />}
        {messages.error && (
          <Text className="mt-3 text-red-400">
            {extractErrorMessage(messages.error, 'erro ao carregar')}
          </Text>
        )}
        {!messages.isLoading && (
          <div className="mt-3 max-h-[600px] space-y-1 overflow-y-auto">
            {messages.data?.items.length === 0 && (
              <Text className="text-slate-400">Sem mensagens no filtro.</Text>
            )}
            {messages.data?.items.map((m) => (
              <details key={m.id} className="rounded border border-slate-800 bg-slate-900/40">
                <summary className="cursor-pointer px-3 py-2 text-xs">
                  <Badge color={m.role === 'user' ? 'blue' : m.role === 'assistant' ? 'emerald' : 'slate'} size="xs">
                    {m.role}
                  </Badge>
                  <span className="ml-2 text-slate-300">
                    {m.user_email ?? `visitante`}
                  </span>
                  {m.model_name && (
                    <code className="ml-2 rounded bg-slate-800 px-1 text-[10px] text-slate-400">
                      {m.model_name}
                    </code>
                  )}
                  <span className="ml-3 text-slate-400 line-clamp-1">
                    {m.content.slice(0, 120)}
                  </span>
                  <span className="float-right text-slate-500">
                    {m.created_at && new Date(m.created_at).toLocaleString('pt-BR')}
                  </span>
                </summary>
                <div className="border-t border-slate-800 p-3 text-xs">
                  <pre className="whitespace-pre-wrap text-slate-200">{m.content}</pre>
                  {m.error_message && (
                    <div className="mt-2 rounded bg-red-900/30 p-2 text-red-300">
                      Erro: {m.error_message}
                    </div>
                  )}
                  <div className="mt-2 text-[10px] text-slate-500">
                    interaction: <code>{m.interaction_id}</code>
                    {m.org_name && ` · org: ${m.org_name}`}
                  </div>
                </div>
              </details>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
